import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { Session, User, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { setSentryUser, clearSentryUser, captureError, logger } from '@/lib/sentry';
import posthog from 'posthog-js';
import type { AuthContextValue, AuthUser } from './types';

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Parse a full name into first and last name parts
 */
function parseFullName(fullName: string | null | undefined): { firstName: string | null; lastName: string | null } {
  if (!fullName || typeof fullName !== 'string') {
    return { firstName: null, lastName: null };
  }
  
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) {
    return { firstName: null, lastName: null };
  }
  
  // First part is first name, rest is last name
  const firstName = parts[0] || null;
  const lastName = parts.length > 1 ? parts.slice(1).join(' ') : null;
  
  return { firstName, lastName };
}

function mapSupabaseUser(user: User | null): AuthUser | null {
  if (!user) return null;
  
  // Extract name from user metadata if available
  const metadata = user.user_metadata || {};
  
  // Try explicit first/last name fields first
  let firstName = metadata.first_name || metadata.firstName || null;
  let lastName = metadata.last_name || metadata.lastName || null;
  
  // If no explicit names, try to parse from full_name or name (common in OAuth providers)
  if (!firstName && !lastName) {
    const fullName = metadata.full_name || metadata.name;
    const parsed = parseFullName(fullName);
    firstName = parsed.firstName;
    lastName = parsed.lastName;
  }
  
  return {
    id: user.id,
    email: user.email,
    firstName,
    lastName,
    imageUrl: metadata.avatar_url || metadata.picture || null,
  };
}

interface SupabaseAuthProviderProps {
  children: ReactNode;
}

export function SupabaseAuthProvider({ children }: SupabaseAuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth
      .getSession()
      .then(({ data: { session: initialSession } }) => {
        setSession(initialSession);
        const mappedUser = mapSupabaseUser(initialSession?.user ?? null);
        setUser(mappedUser);
        setLoading(false);

        // Set initial Sentry user context
        if (mappedUser) {
          setSentryUser({ id: mappedUser.id, email: mappedUser.email ?? undefined });
        }
      })
      .catch((err) => {
        // Don't leave the app stuck in a loading state if session lookup fails.
        logger.error('Failed to load initial Supabase session', { error: String(err) });
        setLoading(false);
      });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, newSession: Session | null) => {
        setSession(newSession);
        const mappedUser = mapSupabaseUser(newSession?.user ?? null);
        setUser(mappedUser);
        setLoading(false);

        // Keep Sentry user context in sync with auth state
        if (mappedUser) {
          setSentryUser({ id: mappedUser.id, email: mappedUser.email ?? undefined });
          if (event === 'SIGNED_IN') {
            posthog.identify(mappedUser.id, { email: mappedUser.email ?? undefined });
          }
        } else {
          clearSentryUser();
          if (event === 'SIGNED_OUT') {
            posthog.reset();
          }
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        const { error: localError } = await supabase.auth.signOut({ scope: 'local' });
        if (localError) {
          throw localError;
        }
      }

      setSession(null);
      setUser(null);
      clearSentryUser();
    } catch (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), {
        feature: 'auth',
        action: 'signOut',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setLoading(false);
      throw error;
    }
    // State will be updated by onAuthStateChange
  }, []);

  const signUp = useCallback(async (
    email: string,
    password: string,
    options?: { fullName?: string; redirectTo?: string },
  ) => {
    setLoading(true);
    // Use env variable for redirect URL (allows override in .env.local for development)
    const baseUrl = import.meta.env.VITE_AUTH_REDIRECT_BASE_URL || window.location.origin;
    const redirectTo = options?.redirectTo || `${baseUrl}/auth/callback?flow=signup`;
    const { error } = await supabase.auth.signUp({ 
      email, 
      password,
      options: {
        emailRedirectTo: redirectTo,
        data: options?.fullName ? { full_name: options.fullName } : undefined,
      }
    });
    if (error) {
      setLoading(false);
      throw error;
    }
    // State will be updated by onAuthStateChange
  }, []);

  // Build full name from user data
  const userName = user 
    ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email?.split('@')[0] || null
    : null;

  const value: AuthContextValue = {
    user,
    session: session ? { user: user!, accessToken: session.access_token } : null,
    loading,
    isSignedIn: !!session && !!user,
    userId: user?.id ?? null,
    userName,
    signOut,
    signIn,
    signUp,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useSupabaseAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider');
  }
  return context;
}

